import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  from,
  Observable,
  tap,
  throwError,
  take,
} from 'rxjs';
import { Router } from '@angular/router';
import {
  fetchAuthSession,
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  decodeJWT,
  updatePassword,
  UpdatePasswordInput,
  fetchMFAPreference,
  updateMFAPreference,
  UpdateMFAPreferenceInput,
  resetPassword,
  confirmResetPassword,
  ConfirmResetPasswordInput,
  ConfirmSignUpInput,
  updateUserAttribute,
  UpdateUserAttributeInput,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  getCurrentUser,
  signOut,
  setUpTOTP,
  verifyTOTPSetup,
  confirmSignIn,
  deleteUser,
} from 'aws-amplify/auth';
import {
  ACCESS_TOKEN_KEY,
  ID_TOKEN_KEY,
} from 'src/app/shared/constants/auth_constants';
import { LoginDetailsService } from '../login/services/login-details.service';
import { LoaderService } from 'src/app/common/services/loader/loader.service';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthErrors } from './auth.errors';
import { LocalstorageService } from 'src/app/shared/providers/localstorage.service';
import { DialogService } from '@ds/ng-wt-dialog';
import { appId } from 'src/resources/app-config.json';
import * as CryptoJS from 'crypto-js';
import { encryptionKey } from 'src/app/const/constant';
import { DialogCleanupService } from './dialog-cleanup.service';
import { ContentsService } from 'src/app/shared/services/contents.service';

declare var window: any;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  appId = appId;

  visitedUrl: string = '';



  private isRefreshing = false;
  public tokenRefreshed$ = new BehaviorSubject<string | null>(null);
  
  // Rate limiting for refresh attempts
  private refreshAttempts = 0;
  private refreshAttemptWindow = Date.now();
  private readonly MAX_REFRESH_ATTEMPTS = 5; // Max 5 refresh attempts
  private readonly REFRESH_ATTEMPT_WINDOW_MS = 60000; // Per 60 seconds

  constructor(
    public route: Router,
    private loginDetailsService: LoginDetailsService,
    private loaderService: LoaderService,
    private toast: NgToastTranslocoService,
    private localStorageService: LocalstorageService,
    private dsDialog: DialogService,
    private dialogCleanupService: DialogCleanupService
  ) {
    //  an event listener to listen for changes in localStorage (across tabs)
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === ACCESS_TOKEN_KEY && !event.newValue) {
        this.navigateToLoginPage();
      }
    });
  }

  private refreshTimerId: any = null;

  setAuthToken(tokenKey: string, tokenValue: string): void {
    this.localStorageService.saveData(tokenKey, tokenValue);
  }

  getAuthToken(tokenKey: string): string | null {
    return this.localStorageService.getData(tokenKey);
  }

  canNavigateToDashboard(): boolean {
    const authToken = this.getAuthToken(ID_TOKEN_KEY);
    const playerId = this.loginDetailsService.playerId;

    if (
      authToken &&
      playerId &&
      authToken !== undefined &&
      playerId !== undefined &&
      playerId !== 'undefined'
    )
      return true;
    return false;
  }

  startRefreshTimerIfTokenInvalid() {
    const authToken = this.getAuthToken(ID_TOKEN_KEY);
    const playerId = this.loginDetailsService.playerId;

    if (
      authToken &&
      playerId &&
      authToken !== undefined &&
      playerId !== undefined &&
      playerId !== 'undefined'
    ) {
      if (!this.refreshTimerId) {
        this.startTokenRefreshTimer();
      }
    }
  }

  isValidToken(tokenKey: string): boolean {
    const token = this.localStorageService.getData(tokenKey);
    if (!token) {
      return false;
    }

    try {
      const decoded = decodeJWT(token);
      const exp = decoded.payload.exp; // Expiration timestamp
      if (!exp) {
        return false;
      }
      const now = Math.floor(Date.now() / 1000);
      return exp > now; // Check if token is actually valid
    } catch (error) {
      console.error('Invalid token format:', error);
      return false;
    }
  }

  async isTokenNearingExpiry(): Promise<boolean> {
    const tokenRefreshTime = await this.getRefreshTime();

    return tokenRefreshTime < 5000;
  }

  /**
   * Sign in a user with AWS Cognito using fetchAuthSession.
   * @param username - The user's username.
   * @param password - The user's password.
   * @returns The authenticated user session if successful.
   */
  // Sign in the user via AWS Cognito and store the tokens
  // Assuming decodeJWT is your custom function to decode JWTs
  decodeJWT(token: string): any {
    const base64Url = token.split('.')[1]; // JWT is typically split into three parts
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // Handle URL-safe base64
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  /**
   * Encrypts password using AES-256-CBC encryption
   * @param plainText - The password to encrypt
   * @param passphrase - The passphrase (appId) to use as encryption key
   * @param ivString - Optional IV string, defaults to 16 zero bytes
   * @returns Encrypted password as hex string
   */
  encryptAes256Cbc(
    plainText: string,
    passphrase: string,
    ivString?: string
  ): string {
    // Key = SHA256(passphrase)
    const keyBytes = CryptoJS.SHA256(passphrase);

    // IV = provided string (utf8, padded/truncated to 16 bytes) or 16 zero bytes
    let ivBytes: CryptoJS.lib.WordArray;
    if (ivString) {
      // Convert IV string to bytes and pad/truncate to 16 bytes
      const ivUtf8 = CryptoJS.enc.Utf8.parse(ivString);
      const paddedIv = new Array(16).fill(0);
      const ivArray = ivUtf8.words;

      for (let i = 0; i < 4 && i < ivArray.length; i++) {
        const word = ivArray[i];
        paddedIv[i * 4] = (word >>> 24) & 0xff;
        paddedIv[i * 4 + 1] = (word >>> 16) & 0xff;
        paddedIv[i * 4 + 2] = (word >>> 8) & 0xff;
        paddedIv[i * 4 + 3] = word & 0xff;
      }

      ivBytes = CryptoJS.lib.WordArray.create(paddedIv.slice(0, 16));
    } else {
      // 16 zero bytes
      ivBytes = CryptoJS.lib.WordArray.create(new Array(16).fill(0));
    }

    // Encrypt using AES-256-CBC with PKCS7 padding
    const encrypted = CryptoJS.AES.encrypt(plainText, keyBytes, {
      iv: ivBytes,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Return as hex string
    return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
  }

  /**
   * Decrypts hex string using AES-256-CBC decryption
   * @param encryptedHex - The encrypted hex string to decrypt
   * @param passphrase - The passphrase (appId) to use as decryption key
   * @param ivString - Optional IV string, defaults to 16 zero bytes
   * @returns Decrypted plaintext string
   */
  decryptAes256Cbc(
    encryptedHex: string,
    passphrase: string,
    ivString?: string
  ): string {
    // Key = SHA256(passphrase)
    const keyBytes = CryptoJS.SHA256(passphrase);

    // IV = provided string (utf8, padded/truncated to 16 bytes) or 16 zero bytes
    let ivBytes: CryptoJS.lib.WordArray;
    if (ivString) {
      // Convert IV string to bytes and pad/truncate to 16 bytes
      const ivUtf8 = CryptoJS.enc.Utf8.parse(ivString);
      const paddedIv = new Array(16).fill(0);
      const ivArray = ivUtf8.words;

      for (let i = 0; i < 4 && i < ivArray.length; i++) {
        const word = ivArray[i];
        paddedIv[i * 4] = (word >>> 24) & 0xff;
        paddedIv[i * 4 + 1] = (word >>> 16) & 0xff;
        paddedIv[i * 4 + 2] = (word >>> 8) & 0xff;
        paddedIv[i * 4 + 3] = word & 0xff;
      }

      ivBytes = CryptoJS.lib.WordArray.create(paddedIv.slice(0, 16));
    } else {
      // 16 zero bytes
      ivBytes = CryptoJS.lib.WordArray.create(new Array(16).fill(0));
    }

    // Create ciphertext from hex string
    const ciphertext = CryptoJS.enc.Hex.parse(encryptedHex);

    // Decrypt using AES-256-CBC with PKCS7 padding
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext } as any,
      keyBytes,
      {
        iv: ivBytes,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    // Return as UTF-8 string
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  /**
   * Encrypts plaintext using AES-256-GCM encryption (matches Dart/Flutter implementation)
   * @param plainText - The text to encrypt
   * @param passphrase - The passphrase to use as encryption key
   * @param useRandomNonce - Whether to use random nonce (currently not implemented, always false)
   * @returns Promise that resolves to encrypted data as hex string (nonce || ciphertext+tag)
   */
  async encryptAes256Gcm(
    plainText: string,
    passphrase: string,
    useRandomNonce: boolean = true
  ): Promise<string> {
    // Step 1: Create 256-bit key from passphrase using SHA-256
    const passphraseBytes = new TextEncoder().encode(passphrase);
    const keyHashBuffer = await crypto.subtle.digest('SHA-256', passphraseBytes);
    const keyBytes = new Uint8Array(keyHashBuffer);

    // Step 2: Create 12-byte nonce by hashing (keyBytes + plainText) and taking first 12 bytes
    const plainTextBytes = new TextEncoder().encode(plainText);
    const combinedBytes = new Uint8Array(keyBytes.length + plainTextBytes.length);
    combinedBytes.set(keyBytes, 0);
    combinedBytes.set(plainTextBytes, keyBytes.length);

    const nonceHashBuffer = await crypto.subtle.digest('SHA-256', combinedBytes);
    const nonce = new Uint8Array(nonceHashBuffer).slice(0, 12);

    // Step 3: Import the key for AES-GCM
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    // Step 4: Encrypt using AES-GCM
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        tagLength: 128 // 16 bytes tag
      },
      cryptoKey,
      plainTextBytes
    );

    const encryptedBytes = new Uint8Array(encryptedBuffer);

    // Step 5: Combine nonce + ciphertext+tag
    const result = new Uint8Array(nonce.length + encryptedBytes.length);
    result.set(nonce, 0);
    result.set(encryptedBytes, nonce.length);

    // Step 6: Convert to hex string
    return Array.from(result)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Decrypts hex string using AES-256-GCM decryption (matches Dart/Flutter implementation)
   * @param encryptedHex - The encrypted hex string (nonce || ciphertext+tag)
   * @param passphrase - The passphrase used for encryption
   * @returns Promise that resolves to decrypted plaintext string
   */
  async decryptAes256Gcm(
    encryptedHex: string,
    passphrase: string
  ): Promise<string> {
    // Step 1: Convert hex string to bytes
    const encryptedBytes = new Uint8Array(
      encryptedHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
    );

    // Step 2: Extract nonce (first 12 bytes) and encrypted data (rest)
    const nonce = encryptedBytes.slice(0, 12);
    const encryptedData = encryptedBytes.slice(12);

    // Step 3: Create key from passphrase using SHA-256
    const passphraseBytes = new TextEncoder().encode(passphrase);
    const keyHashBuffer = await crypto.subtle.digest('SHA-256', passphraseBytes);
    const keyBytes = new Uint8Array(keyHashBuffer);

    // Step 4: Import the key for AES-GCM
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );

    // Step 5: Decrypt using AES-GCM
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        tagLength: 128 // 16 bytes tag
      },
      cryptoKey,
      encryptedData
    );

    // Step 6: Convert to string
    return new TextDecoder().decode(decryptedBuffer);
  }

  async signIn(
    username: string,
    password: string
  ): Promise<{
    success: boolean;
    hasAPIFacedError?: boolean;
    redirect?: string;
    isEmailNotFound?: boolean;
    isUserDisabled?: boolean;
    newPasswordRequired?: boolean;
    userAttributes?: any;
    mfaChallenge?: {
      challengeName: string;
      challengeParameters: any;
      session: string;
    };
  }> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      // Attempt to sign in with username and password if no tokens found
      const appId = this.appId;
      const encryptedPassword = await this.encryptAes256Gcm(password, encryptionKey);
      const signInOutput = await signIn({
        username,
        password: encryptedPassword,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });

      const { isSignedIn, nextStep } = signInOutput;

      const isAPIFacedError = localStorage.getItem('hasAPIFacedError');
      if (isAPIFacedError === 'true') {
        return { success: false, hasAPIFacedError: true };
      }

      // Check if MFA is required or new password is required
      if (nextStep && nextStep.signInStep) {
        const challengeStep = nextStep.signInStep;

        // Handle NEW_PASSWORD_REQUIRED challenge
        if (challengeStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          return {
            success: false,
            newPasswordRequired: true,
            userAttributes: nextStep.missingAttributes || [],
          };
        }

        // Handle different MFA challenges
        if (challengeStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          // Normalize the structure to include CODE_DELIVERY_DESTINATION
          const challengeParameters: any = {};
          if (nextStep.codeDeliveryDetails) {
            challengeParameters.CODE_DELIVERY_DESTINATION = nextStep.codeDeliveryDetails.destination;
            challengeParameters.CODE_DELIVERY_DELIVERY_MEDIUM = nextStep.codeDeliveryDetails.deliveryMedium;
          }

          return {
            success: false,
            mfaChallenge: {
              challengeName: 'SMS_MFA',
              challengeParameters: challengeParameters,
              session: '', // Amplify handles session internally
            },
          };
        } else if (challengeStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          return {
            success: false,
            mfaChallenge: {
              challengeName: 'SOFTWARE_TOKEN_MFA',
              challengeParameters: {},
              session: '', // Amplify handles session internally
            },
          };
        } else if (challengeStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
          // When both SMS and TOTP are available, include phone number from codeDeliveryDetails
          const challengeParameters: any = {
            allowedMFATypes: nextStep.allowedMFATypes || [],
          };

          // Include phone number if available (for SMS option)
          // @ts-ignore - TypeScript doesn't recognize codeDeliveryDetails on this type
          if (nextStep.codeDeliveryDetails) {
            // @ts-ignore
            challengeParameters.CODE_DELIVERY_DESTINATION = nextStep.codeDeliveryDetails.destination;
            // @ts-ignore
            challengeParameters.CODE_DELIVERY_DELIVERY_MEDIUM = nextStep.codeDeliveryDetails.deliveryMedium;
          }

          return {
            success: false,
            mfaChallenge: {
              challengeName: 'SELECT_MFA_TYPE',
              challengeParameters: challengeParameters,
              session: '', // Amplify handles session internally
            },
          };
        }
      }

      if (isSignedIn) {
        // Check if migration is completed before proceeding
        try {
          const userAttributes = await fetchUserAttributes();
          const isMigrationCompleted = userAttributes['custom:ismigrationcompleted'];

          const emailAttribute: string = userAttributes['email'] || '';
          this.loginDetailsService.setPlayerUsername(emailAttribute);

          if (isMigrationCompleted === 'false') {
            // User is signed in but needs to complete migration
            // Store session details first so navigation works after password update
            const session = await fetchAuthSession();
            const accessToken = session.tokens?.accessToken?.toString() || '';
            const idToken = session.tokens?.idToken?.toString() || '';
            const decodedToken: any = decodeJWT(idToken);
            const givenName = decodedToken?.payload?.given_name;
            this.loginDetailsService.setPlayerId(givenName);
            this.loginDetailsService.setPlayerUsername(username);
            this.setAuthToken(ID_TOKEN_KEY, idToken);
            this.setAuthToken(ACCESS_TOKEN_KEY, accessToken);

            return {
              success: false,
              newPasswordRequired: true,
              userAttributes: [],
            };
          }
        } catch (error) {
          console.error('Error fetching user attributes for migration check:', error);
          // Continue with sign-in if attribute fetch fails
        }

        this.startTokenRefreshTimer();
        return await this.loadSessionDetails(username, password);
      } else {
        return { success: false, redirect: 'otp_step' };
      }
    } catch (error: any) {
      return await this.handleSignInErrors(username, password, error);
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  private async canAllowToContinue(userName: string): Promise<boolean> {
    const { username, userId, signInDetails } = await getCurrentUser();

    const loginId = signInDetails?.loginId;

    return !!(loginId && loginId === userName);
  }

  private async loginInAsNewUser(
    username: string,
    password: string
  ): Promise<boolean> {
    try {
      await signOut();
      const appId = this.appId;
      const encryptedPassword = await this.encryptAes256Gcm(password, encryptionKey);
      const { isSignedIn } = await signIn({
        username,
        password: encryptedPassword,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });
      return isSignedIn;
    } catch (error) {
      const errorData = await this.handleSignInErrors(
        username,
        password,
        error
      );
      return errorData.success;
    }
  }

  private async loadSessionDetails(username: string, password: string) {
    if (await this.canAllowToContinue(username)) {
    } else {
      let loggedIn = await this.loginInAsNewUser(username, password);

      if (!loggedIn) {
        return { success: false };
      }
    }

    const session = await fetchAuthSession();

    const accessToken = session.tokens?.accessToken?.toString() || '';
    const idToken = session.tokens?.idToken?.toString() || '';
    const decodedToken: any = decodeJWT(idToken);
    const givenName = decodedToken?.payload?.given_name;
    this.loginDetailsService.setPlayerId(givenName);
    this.loginDetailsService.setPlayerUsername(username);
    this.setAuthToken(ID_TOKEN_KEY, idToken);
    this.setAuthToken(ACCESS_TOKEN_KEY, accessToken);
    if (givenName !== undefined) {
      this.navigateToDashboard();
    }
    return { success: true };
  }

  private async handleSignInErrors(
    username: string,
    password: string,
    error: any
  ): Promise<{
    success: boolean;
    redirect?: string;
    isEmailNotFound?: boolean;
    isUserDisabled?: boolean;
  }> {
    if (error && error.name) {
      switch (error.name) {
        case AuthErrors.NotAuthorizedException:
          if (error.message && error.message === 'User is disabled.') {
            return { success: false, isUserDisabled: true };
          }
          this.toast.toastError('MODULES.LOGIN.ERROR_MESSAGE_LOGINPAGE');
          return { success: false };

        case AuthErrors.UserAlreadyAuthenticatedException:
          return await this.loadSessionDetails(username, password);

        case AuthErrors.UserLambdaValidationException:
          this.toast.toastError('MODULES.LOGIN.OTP_USER_VERIFICATION');
          return { success: false };

        case AuthErrors.UserNotFoundException:
          let errorName = error.toString();
          let errorNameLowerCase = errorName.toLowerCase();
          if (errorNameLowerCase.includes('usermigrationemailrequirederror')) {
            return { success: false, isEmailNotFound: true };
          } else {
            this.toast.toastError('MODULES.LOGIN.JUST_USER_NOT_FOUND');
            return { success: false };
          }

        case AuthErrors.UserUnAuthenticatedException:
          return { success: false, redirect: 'otp_step' };

        default:
          break;
      }
    }

    this.toast.toastError('MODULES.LOGIN.GENERIC_SIGNIN_ERROR');
    return { success: false };
  }

  private generateGuid(email: string): string {
    const now = new Date();
    const timestamp = now.getTime().toString();
    const random = (now.getTime() % 1000000).toString().padStart(6, '0');
    const emailPrefix = email.split('@')[0];
    return `${emailPrefix}_${timestamp}_${random}`;
  }

  sendOtp(
    email: string,
    password: string,
    accountNumber?: string
  ): Observable<any> {
    return new Observable((observer) => {
      this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
      const guid = this.generateGuid(email);
      localStorage.setItem('guid_email', guid);

      this.encryptAes256Gcm(password, encryptionKey)
        .then((encryptedPassword) => {
          return signUp({
            username: guid,
            password: encryptedPassword,
            options: {
              userAttributes: {
                email: email,
                given_name: accountNumber || '',
              },
            },
          });
        })
        .then((data) => {
          observer.next(data);
          observer.complete();
        })
        .catch((err) => {
          if (
            err &&
            (err.code === 'UsernameExistsException' ||
              err.message.includes('User already exists') || err.message.includes('UserAlreadyExistsException'))
          ) {
            this.toast.toastError('MODULES.LOGIN.USER_ALREADY_EXISTS');
          } else {
            this.toast.toastError('MODULES.LOGIN.GENERIC_SIGNUP_ERROR');
          }
          observer.error(err);
        })
        .finally(() => {
          this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
        });
    });
  }

  resendSignUpCode(email: string, password: string, accountNumber?: string): Observable<any> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    const guid = localStorage.getItem('guid_email') || '';
    return from(resendSignUpCode({ username: guid })).pipe(
      catchError((err) => {
        this.toast.toastError('Failed to send OTP');
        return throwError(() => this.getErrorMessage(err));
      })
    );
  }

  private getErrorMessage(error: any): string {
    if (error.code === 'NetworkError') {
      return 'Network error. Please try again later.';
    }
    return error.message || 'An unexpected error occurred.';
  }

  async verifyOtpSignUp(email: string, otpCode: string): Promise<any> {
    if (!otpCode || otpCode.length !== 6) {
      this.toast.toastError('MODULES.LOGIN.VALIDDATE_OTP');
      return;
    }
    const guid = localStorage.getItem('guid_email')
      ? localStorage.getItem('guid_email')
      : email;
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    const confirmSignUpInput = {
      username: guid,
      confirmationCode: otpCode,
    };

    try {
      const confirmSignUpWithUsername = { ...confirmSignUpInput, username: confirmSignUpInput.username || '' };
      const data = await confirmSignUp(confirmSignUpWithUsername as ConfirmSignUpInput);
      return data;
    } catch (err) {
      console.error('Error caught:', err);
      if (
        (err && (err as any)?.message && (err as any)?.message.includes('CodeMismatchException')) ||
        (err as any)?.name === 'CodeMismatchException'
      ) {
        this.toast.toastError('MODULES.LOGIN.VARIFIYING_OTP');
      } else {
        this.toast.toastError('MODULES.LOGIN.GENERIC_OTP_ERROR');
      }
      throw err;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async refreshTokens(): Promise<string> {
    // Rate limiting check
    const now = Date.now();
    if (now - this.refreshAttemptWindow > this.REFRESH_ATTEMPT_WINDOW_MS) {
      // Window expired, reset
      this.refreshAttempts = 0;
      this.refreshAttemptWindow = now;
    }

    if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
      console.error('[Auth] Max refresh attempts exceeded - forcing logout');
      this.logoutSignedInUser();
      throw new Error('Max refresh attempts exceeded');
    }

    this.refreshAttempts++;

    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.tokenRefreshed$
          .pipe(
            filter((token) => token !== null),
            take(1)
          )
          .subscribe({
            next: (token) => resolve(token!),
            error: (err) => reject(err),
          });
      });
    }

    this.isRefreshing = true;
    this.tokenRefreshed$.next(null);

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const accessToken = session.tokens?.accessToken?.toString() || '';
      const idToken = session.tokens?.idToken?.toString() || '';

      if (!accessToken || !idToken) {
        console.error('Invalid tokens received');
        this.logoutSignedInUser();
        return '';
      }
      this.setAuthToken(ID_TOKEN_KEY, idToken);
      this.setAuthToken(ACCESS_TOKEN_KEY, accessToken);
      this.startTokenRefreshTimer();
      this.tokenRefreshed$.next(idToken);
      return idToken;
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      const failedSubject = this.tokenRefreshed$;
      this.tokenRefreshed$ = new BehaviorSubject<string | null>(null);
      failedSubject.error(error);
      this.logoutSignedInUser();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      let baseUrl = localStorage.getItem('baseUrl');
      this.localStorageService.clearAllData();
      localStorage.setItem('baseUrl', baseUrl || '');
      document.cookie = '';
    }
  }
  registerUser = async (username: string, confirmationCode: string) => {
    try {
      const user = await confirmSignUp({
        username,
        confirmationCode,
      });
    } catch (error) {
      console.error('Error during registration: ', error);
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  };
  async getCurrentUserSession(): Promise<any> {
    try {
      const session = await fetchAuthSession();
      if (
        !session ||
        !session.credentials ||
        !session.credentials.accessKeyId
      ) {
        return null;
      }
      return session;
    } catch (error: any) {
      console.error('No authenticated session', error);
      return null;
    }
  }

  navigateToDashboard() {
    const routerName = this.loginDetailsService.initialContentCategoryName;
    const categoryKey =
      this.loginDetailsService.initialContentCategoryKey || '';
    this.route.navigate([`/dynamic-content/${routerName}`], {
      state: categoryKey,
    });
  }

  navigateToLoginPage(stateData?: any) {
    if (stateData) {
      this.route.navigate(['/login'], { state: stateData });
    } else {
      this.route.navigate(['/login']);
    }
  }

  async updatePlayerPassword(queryList: UpdatePasswordInput): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await updatePassword(queryList);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async getPlayerMfaPreference(): Promise<any> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await fetchMFAPreference();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async updatePlayerMfaPreference(isMfaEnabled: boolean, mfaMethod: string): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      // Get current MFA preference to preserve the other method's state
      const currentPreference = await fetchMFAPreference();

      let preference: UpdateMFAPreferenceInput;

      if (isMfaEnabled) {
        if (mfaMethod === 'SMS') {
          // Enable SMS, keep TOTP state if already enabled
          const totpEnabled = currentPreference?.enabled?.includes('TOTP') || currentPreference?.preferred === 'TOTP';
          preference = {
            sms: 'ENABLED',
            totp: totpEnabled ? 'ENABLED' : 'DISABLED'
          };
        } else {
          // Enable TOTP, keep SMS state if already enabled
          const smsEnabled = currentPreference?.enabled?.includes('SMS') || currentPreference?.preferred === 'SMS';
          preference = {
            totp: 'ENABLED',
            sms: smsEnabled ? 'ENABLED' : 'DISABLED'
          };
        }
      } else {
        // Disable only the specified method
        if (mfaMethod === 'SMS') {
          const totpEnabled = currentPreference?.enabled?.includes('TOTP') || currentPreference?.preferred === 'TOTP';
          preference = {
            sms: 'DISABLED',
            totp: totpEnabled ? 'ENABLED' : 'DISABLED'
          };
        } else {
          const smsEnabled = currentPreference?.enabled?.includes('SMS') || currentPreference?.preferred === 'SMS';
          preference = {
            totp: 'DISABLED',
            sms: smsEnabled ? 'ENABLED' : 'DISABLED'
          };
        }
      }

      await updateMFAPreference(preference);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async disableAllMfa(): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      // Verify user is authenticated first
      const session = await fetchAuthSession();
      if (!session || !session.tokens || !session.tokens.idToken) {
        console.error('disableAllMfa: No valid authentication session found');
        throw new Error('User must be authenticated to disable MFA');
      }

      // Verify tokens are not expired
      const idToken = session.tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp && idToken.payload.exp < currentTime) {
        console.error('disableAllMfa: Authentication token has expired');
        throw new Error('Authentication session expired. Please sign in again.');
      }

      console.log('disableAllMfa: Valid session found, proceeding to disable MFA');

      // Disable both SMS and TOTP
      const preference: UpdateMFAPreferenceInput = {
        sms: 'DISABLED',
        totp: 'DISABLED'
      };

      await updateMFAPreference(preference);
      console.log('disableAllMfa: MFA successfully disabled');
    } catch (error) {
      console.error('disableAllMfa: Error occurred', error);
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async confirmNewPassword(
    newPassword: string,
    username: string
  ): Promise<{ success: boolean }> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      // Encrypt the new password before sending
      const encryptedPassword = await this.encryptAes256Gcm(newPassword, encryptionKey);

      const { isSignedIn } = await confirmSignIn({
        challengeResponse: encryptedPassword
      });

      if (isSignedIn) {
        this.startTokenRefreshTimer();
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString() || '';
        const idToken = session.tokens?.idToken?.toString() || '';
        const decodedToken: any = decodeJWT(idToken);
        const givenName = decodedToken?.payload?.given_name;

        this.loginDetailsService.setPlayerId(givenName);
        this.loginDetailsService.setPlayerUsername(username);
        this.setAuthToken(ID_TOKEN_KEY, idToken);
        this.setAuthToken(ACCESS_TOKEN_KEY, accessToken);

        if (givenName !== undefined) {
          this.navigateToDashboard();
        }

        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      console.error('Error confirming new password:', error);
      this.toast.toastError('Failed to set new password. Please try again.');
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async confirmMfaSignIn(
    otpCode: string,
    mfaType?: 'SMS' | 'TOTP'
  ): Promise<{ success: boolean; username?: string; password?: string }> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: otpCode,
        options: mfaType ? {
          // TOTP uses 'SOFTWARE_TOKEN_MFA', SMS uses 'SMS_MFA'
          // @ts-ignore - Amplify types might not perfectly match
          ...(mfaType === 'TOTP' ? {} : {})
        } : undefined
      });

      if (isSignedIn) {
        this.startTokenRefreshTimer();
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString() || '';
        const idToken = session.tokens?.idToken?.toString() || '';
        const decodedToken: any = decodeJWT(idToken);
        const givenName = decodedToken?.payload?.given_name;
        const username = decodedToken?.payload?.email || decodedToken?.payload?.['cognito:username'];

        this.loginDetailsService.setPlayerId(givenName);
        this.loginDetailsService.setPlayerUsername(username);
        this.setAuthToken(ID_TOKEN_KEY, idToken);
        this.setAuthToken(ACCESS_TOKEN_KEY, accessToken);

        if (givenName !== undefined) {
          this.navigateToDashboard();
        }

        return { success: true, username };
      }
      return { success: false };
    } catch (error: any) {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async selectMfaType(mfaType: 'SMS' | 'TOTP'): Promise<{ phoneNumber?: string }> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      // When user selects MFA type, confirm with the selection
      // AWS Amplify expects 'SMS', 'TOTP', or 'EMAIL' as the challenge response
      const output = await confirmSignIn({
        challengeResponse: mfaType
      });

      // Extract phone number from the next step if SMS was selected
      let phoneNumber: string | undefined;
      if (output.nextStep && 'codeDeliveryDetails' in output.nextStep) {
        // @ts-ignore - TypeScript doesn't recognize the conditional type properly
        phoneNumber = output.nextStep.codeDeliveryDetails?.destination;
      }

      return { phoneNumber };
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async resetPlayerPassword(email: string) {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await resetPassword({ username: email });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async resetPlayerPasswordWithValidation(
    email: string,
    validationData: {
      accountNumber: string;
      dateOfBirth: string;
      zipCode: string;
    }
  ) {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await resetPassword({
        username: email,
        options: {
          clientMetadata: {
            'account number': validationData.accountNumber,
            dob: validationData.dateOfBirth,
            pincode: validationData.zipCode,
          },
        },
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async confirmPlayerPasswordReset(resetQuery: ConfirmResetPasswordInput) {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await confirmResetPassword(resetQuery);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async setUserAttributes(attribute: any) {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      const userAttributes: UpdateUserAttributeInput = {
        userAttribute: {
          attributeKey: attribute,
          value: '',
        },
      };
      return await updateUserAttribute(userAttributes);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async deleteUserAccount() {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await deleteUser();
    } catch (error: any) {
      this.toast.toastError('MODULES.COMMON.MESSAGES.ERROR_WHILE_RETRIEVING_IDENTITY_ID');
      throw new Error(error?.message || String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async updateMigrationStatus(): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'custom:ismigrationcompleted',
          value: 'true',
        },
      });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : String(error));
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async logoutSignedInUser() {
    console.log('[Auth] Logging out signed-in user');
    
    // Use comprehensive dialog cleanup instead of just dsDialog
    this.dialogCleanupService.closeAllDialogs();
    
    try {
      await this.signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      this.clearRefreshTimer();
      this.navigateToLoginPage();
    }
  }

  /**
   * Force logout with comprehensive cleanup
   * Closes ALL dialog types, clears sensitive data, clears storage
   * 
   * @param reason - Reason for logout ('idle_expired', 'invalid_token', 'refresh_token_expired', etc.)
   */
  async forceLogout(reason: string = 'manual') {
    console.log('[Auth] Force logout triggered - reason:', reason);
    
    // CRITICAL: Close ALL open dialogs of all types
    // This must happen BEFORE any navigation
    this.dialogCleanupService.closeAllDialogs();
    
    try {
      // Sign out from AWS Cognito
      await this.signOut();
    } catch (error) {
      console.error('[Auth] Error during force sign out:', error);
    } finally {
      // Clean up timers and state
      this.clearRefreshTimer();
      
      // Clear sensitive authentication data
      this.clearSensitiveData();
      
      // Clear LoginDetailsService data
      this.loginDetailsService.clearAllUserData();
      
      // Store logout reason for display on login page
      this.localStorageService.saveData('logout_reason', reason);
      this.localStorageService.saveData('logout_timestamp', new Date().toISOString());
      
      // Navigate to login page
      this.navigateToLoginPage({ reason });
    }
  }

  /**
   * Clear all sensitive authentication and user data from storage
   * Keeps base configuration (baseUrl, branding, etc.)
   */
  private clearSensitiveData(): void {
    console.log('[Auth] Clearing sensitive data');
    
    // Clear tokens
    this.localStorageService.deleteData(ID_TOKEN_KEY);
    this.localStorageService.deleteData(ACCESS_TOKEN_KEY);
    this.localStorageService.deleteData('refresh_token');
    this.localStorageService.deleteData('id_token');
    this.localStorageService.deleteData('access_token');
    
    // Clear user identification data
    this.localStorageService.deleteData('playerId');
    this.localStorageService.deleteData('playerClubId');
    this.localStorageService.deleteData('userName');
    this.localStorageService.deleteData('EMAIL');
    this.localStorageService.deleteData('mail');
    
    // Clear session storage
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('lastActivityTime');
      sessionStorage.removeItem('idle_warning_shown');
    }
    
    // Clear any cached AWS credentials
    this.localStorageService.deleteData('awsCredentials');
    
    // Clear browser cookies (auth-related)
    document.cookie.split(';').forEach((c) => {
      const eqPos = c.indexOf('=');
      const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
      if (name && (name.includes('token') || name.includes('auth') || name.includes('session'))) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      }
    });
  }

  clearRefreshTimer() {
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
      console.log('Token refresh timer cleared.');
    }
  }

  private async getRefreshTime() {
    const session = await fetchAuthSession();

    const expiryTime = session?.tokens?.idToken?.payload?.exp;
    const expiresIn = (expiryTime ?? 0) - Math.floor(Date.now() / 1000);
    const tokenRefreshBufferTime = window.config?.tokenRefreshBufferTime ?? 120;

    return expiresIn > tokenRefreshBufferTime
      ? (expiresIn - tokenRefreshBufferTime) * 1000
      : 1000; // Refresh 2 minutes before expiry
  }

  async startTokenRefreshTimer() {
    try {
      const { username, signInDetails } = await getCurrentUser();
      const loginId = signInDetails?.loginId;
      if (!loginId && !username) {
        console.error('Error - No username or login details.');
        this.logoutSignedInUser();
        return;
      }

      const refreshTime = await this.getRefreshTime();

      this.refreshTimerId = setTimeout(() => {
        this.clearRefreshTimer();
        this.refreshTokens();
      }, refreshTime);
    } catch (error) {
      console.error('Error starting token refresh timer:', error);
      this.logoutSignedInUser();
    }
  }

  /**
   * Check if refresh token is still valid
   * Used to detect when user returns from system lock/sleep
   * 
   * @returns true if refresh token exists and hasn't expired
   */
  public isRefreshTokenValid(): boolean {
    const refreshToken = this.getAuthToken('refresh_token');
    
    if (!refreshToken) {
      return false;
    }

    try {
      const decoded = decodeJWT(refreshToken);
      const exp = decoded.payload.exp;
      
      if (!exp) {
        return false;
      }

      const now = Math.floor(Date.now() / 1000);
      return exp > now;
    } catch (error) {
      console.error('Error checking refresh token validity:', error);
      return false;
    }
  }

  /**
   * Validate token structure and security
   * Ensures token hasn't been tampered with
   * 
   * @param token The token to validate
   * @param tokenType Type of token (id_token, access_token, refresh_token)
   * @returns true if token passes security validation
   */
  private validateTokenSecurity(token: string, tokenType: string): boolean {
    if (!token) {
      console.warn(`[Auth] ${tokenType} is empty`);
      return false;
    }

    try {
      // JWT should have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn(`[Auth] ${tokenType} has invalid format - expected 3 parts, got ${parts.length}`);
        return false;
      }

      // Decode and validate payload
      const decoded = decodeJWT(token);
      
      if (!decoded || !decoded.payload) {
        console.warn(`[Auth] ${tokenType} payload is invalid`);
        return false;
      }

      const payload = decoded.payload;
      
      // Verify required claims
      if (!payload.sub && !payload.cognito_username) {
        console.warn(`[Auth] ${tokenType} missing subject claim`);
        return false;
      }

      // Verify token hasn't expired
      if (payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp < now) {
          console.warn(`[Auth] ${tokenType} has expired`);
          return false;
        }
      }

      // Verify token was issued in the past (not issued in future)
      if (payload.iat) {
        const now = Math.floor(Date.now() / 1000);
        if (payload.iat > now + 300) { // Allow 5 minute clock skew
          console.warn(`[Auth] ${tokenType} iat claim is in the future`);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error(`[Auth] Error validating ${tokenType} security:`, error);
      return false;
    }
  }

  async getPlayerUserAttributes(): Promise<Record<string, string>> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      const attributes = await fetchUserAttributes();
      return attributes as Record<string, string>;
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async updatePhoneNumber(phoneNumber: string): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: phoneNumber,
        },
      });
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async sendPhoneVerificationCode(): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async confirmPhoneVerification(confirmationCode: string): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode });
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async setupAuthenticatorTOTP(): Promise<{ sharedSecret: string; getSetupUri: (appName: string, accountName?: string) => URL }> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      return await setUpTOTP();
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }

  async verifyAuthenticatorTOTP(code: string): Promise<void> {
    this.loaderService.setLoaderStateActive(true, 'cognito_service_url');
    try {
      await verifyTOTPSetup({ code });
    } catch (error) {
      throw error;
    } finally {
      this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    }
  }
}
